import React, { useCallback } from 'react';
import cn from 'bem-cn';

import { useIsHydrated } from '../../hooks';
import { FeedCardData } from '../types';
import { PromptCard } from '../PromptCard';
import { CourseCard } from '../CourseCard/CourseCard';
import { ChallengeCard } from '../ChallengeCard/ChallengeCard';
import { Collection } from '../Collection';
import { PromptCardSkeleton } from '../PromptCardSkeleton';
import { NewPromptCard } from '../NewPromptCard';
import { usePromptCardPreset } from './usePromptCardPreset';
import { useFeed } from './Feed.hooks';
import { Entity, FeedPlace, FeedProps } from './Feed.types';

import './Feed.scss';

const b = cn('feed');

export const Feed: React.FC<FeedProps> = ({
    collections = [],
    prompts = [],
    challenges = [],
    courses = [],
    className,
    containerClassName,
    columns = 2,
    withHover = true,
    isLoading = false,
    hasMore = true,
    showStatuses = false,
    showNewPromptCard = false,
    needMarkPromptsAsViewed,
    place,
}) => {
    const isLoaded = useIsHydrated();
    const promptCardPreset = usePromptCardPreset(place);

    const displayItems = useFeed(
        {
            collections,
            prompts,
            challenges,
            courses,
        },
        columns,
    );

    const renderPrompt = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => (
            <PromptCard
                className={b('item', { 'type-prompt': true })}
                withHover={withHover}
                prompt={item}
                showStatus={showStatuses}
                needMarkAsViewed={needMarkPromptsAsViewed}
                {...promptCardPreset}
            />
        ),
        [needMarkPromptsAsViewed, promptCardPreset, showStatuses, withHover],
    );

    const renderCollection = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => <Collection collection={item} className={b('item', { 'type-collection': true })} />,
        [],
    );

    const renderChallenge = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => <ChallengeCard className={b('item', { 'type-challenge': true })} challenge={item} alwaysOn />,
        [],
    );

    const renderCourse = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => <CourseCard className={b('item', { 'type-course': true })} course={item} />,
        [],
    );

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderItem = (item: any) => {
        switch (item.type) {
            case Entity.Prompt:
                return renderPrompt(item);
            case Entity.Challenge:
                return renderChallenge(item);
            case Entity.Collection:
                return renderCollection(item);
            case Entity.Course:
                return renderCourse(item);
            default:
                return renderPrompt(item);
        }
    };

    const renderSkeletonCards = useCallback(() => {
        const count = place === FeedPlace.Popular ? columns : Math.max(columns * 3, 8);

        return Array.from({ length: count }, (_, index) => (
            <PromptCardSkeleton
                key={`skeleton-${index}`}
                className={b('item', { 'type-prompt': true })}
                size={promptCardPreset.size}
            />
        ));
    }, [columns, place, promptCardPreset.size]);

    return (
        <div className={containerClassName}>
            <div
                className={b({ loaded: isLoaded }).mix(className)}
                style={
                    {
                        '--columns-count': columns,
                        '--challenge-span': columns === 1 ? 1 : Math.max(Math.floor(columns / 2), 2),
                    } as React.CSSProperties
                }
            >
                {displayItems?.map((item: FeedCardData, index: number) => (
                    <React.Fragment key={`${item.type}-${item.id}-${index}`}>{renderItem(item)}</React.Fragment>
                ))}
                {showNewPromptCard && <NewPromptCard />}
                {isLoading && renderSkeletonCards()}
            </div>
            {!hasMore && prompts.length === 0 && <div className={b('no-results')}>Ничего не нашлось</div>}
        </div>
    );
};
